import { useMemo } from "react";
import { Space, Tag, TreeSelect, Typography } from "antd";
import type { BOMActivity } from "../api-models";
import { activityLabel, activityPath, flattenActivities } from "../helpers/activities";

interface ActivityScopeSelectProps {
  /** The roots of the BOM version's activity tree, as the server sends it. */
  activities: BOMActivity[];
  value: number[];
  onChange: (activityIds: number[]) => void;
  /** Read-only documents render the chosen work as tags instead of a picker. */
  readOnly?: boolean;
  disabled?: boolean;
  placeholder?: string;
  /** Tutorial anchor (tutorials/), landed on the picker itself. */
  "data-tour"?: string;
}

interface ActivityTreeNode {
  value: number;
  title: string;
  children?: ActivityTreeNode[];
}

function toTreeData(nodes: BOMActivity[]): ActivityTreeNode[] {
  return nodes.map((n) => ({
    value: n.id,
    title: activityLabel(n),
    children: n.children?.length ? toTreeData(n.children) : undefined,
  }));
}

// The work a request or consumption record is for, picked from the BOM's own tree at any
// level. Checking a parent covers everything beneath it - SHOW_PARENT is what makes that
// literal: the parent alone is sent, and the server expands it the same way
// (expandActivitySelection, bom_activity.go), so a branch that grows a new activity later
// is still covered by a selection made today.
export function ActivityScopeSelect({ activities, value, onChange, readOnly, disabled, placeholder, "data-tour": dataTour }: ActivityScopeSelectProps) {
  const byId = useMemo(() => flattenActivities(activities), [activities]);
  const treeData = useMemo(() => toTreeData(activities), [activities]);

  if (readOnly) {
    if (value.length === 0) return <Typography.Text type="secondary">-</Typography.Text>;
    return (
      <Space wrap size={[4, 4]}>
        {value.map((id) => (
          <Tag key={id}>{activityPath(id, byId)}</Tag>
        ))}
      </Space>
    );
  }

  return (
    <TreeSelect
      data-tour={dataTour}
      multiple
      treeCheckable
      showCheckedStrategy={TreeSelect.SHOW_PARENT}
      treeDefaultExpandAll
      allowClear
      disabled={disabled}
      style={{ minWidth: 320, maxWidth: "100%" }}
      placeholder={placeholder ?? "Select the activities this is for"}
      value={value}
      treeData={treeData}
      onChange={(ids) => onChange(ids as number[])}
      treeNodeFilterProp="title"
      maxTagCount="responsive"
    />
  );
}
